import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Card from '../Card';
import EmptyState from '../EmptyState';
import Loader from '../../../../common/Loader';
import ShareToChatModal from '../ShareToChatModal';
import ShareToEmailModal from '../ShareToEmailModal';
import HealthRecordSearch from '../HealthRecordSearch';
import { useLazyLoad } from '../../../../../hocs/useLazyLoad';
import * as healthRecord from '../../../../../redux/healthboard/patientPortal/health-record-req';
import {
  selectHealthRecordEntity,
  selectHealthRecordPending,
  selectHealthRecordPaginationPending,
  selectHealthRecordEntityTotal,
} from '../../../../../redux/healthboard/patientPortal/selectors';

import styles from '../index.styl';

const HealthRecordsList = ({ userId, activeTab, formatMessage }) => {
  const dispatch = useDispatch();

  const rootRef = useRef();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const data = useSelector(selectHealthRecordEntity);
  const pending = useSelector(selectHealthRecordPending);
  const paginationPending = useSelector(selectHealthRecordPaginationPending);
  const total = useSelector(selectHealthRecordEntityTotal);
  const listLength = data.length;

  const isNotEmpty = Boolean(data.length);

  const showList = isNotEmpty && !pending;
  const showEmptyState = !isNotEmpty && !pending && !paginationPending;

  const clearData = () => dispatch(healthRecord.actions.clear());

  const fetchData = (withPeriod, withPagination) => {
    const limit = 20;
    const offset = withPagination ? listLength : 0;
    const sendPeriod = withPeriod && startDate && endDate;
    const dateData = {
      ...(sendPeriod && {
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
      }),
    };

    dispatch(
      healthRecord.actions.send({
        limit,
        userId,
        offset,
        withPagination,
        ...dateData,
      }),
    );
  };

  const loadMore = () => {
    const isLastPage = total <= listLength;
    if (isLastPage) return;

    const hasDate = startDate && endDate;
    fetchData(hasDate, true);
  };

  const clearDate = () => {
    setEndDate(null);
    setStartDate(null);
    fetchData(false, false);
  };

  useEffect(() => {
    fetchData(false, false);
    return () => clearData();
  }, []);

  useLazyLoad({
    node: rootRef,
    loadMore,
    path: [],
  });

  return (
    <div className={styles.rootHealthRecords}>
      <HealthRecordSearch
        pending={pending}
        endDate={endDate}
        onClear={clearDate}
        startDate={startDate}
        setEndDate={setEndDate}
        setStartDate={setStartDate}
        formatMessage={formatMessage}
        onSearch={() => fetchData(true, false)}
      />
      {pending && <Loader />}
      {showEmptyState && (
        <EmptyState
          activeTab={activeTab}
          formatMessage={formatMessage}
          withInfo={Boolean(startDate && endDate)}
        />
      )}
      {showList && (
        <div className={styles.paginationHealthRecords} ref={rootRef}>
          {data.map(card => (
            <Card
              key={card.id}
              entity={card}
              activeTab={activeTab}
              formatMessage={formatMessage}
            />
          ))}
          {paginationPending && (
            <div className={styles.paginationLoader}>
              <Loader />
            </div>
          )}
        </div>
      )}
      <ShareToChatModal />
      <ShareToEmailModal />
    </div>
  );
};

HealthRecordsList.propTypes = {
  activeTab: PropTypes.string,
  formatMessage: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default HealthRecordsList;



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/PatientPortal/DataList/HealthRecordsList/index.jsx